import { useEffect } from "react";
import type { Balance, Category, Transaction } from "../api/types";
import { formatCents, formatDate } from "../lib/money";

/** How long the celebration stays up before dismissing itself. */
const AUTO_DISMISS_MS = 9000;

/**
 * How many arrivals to name individually.
 *
 * A child logging back in after a long gap gets a party, not a bank statement —
 * past this the rest collapse into one line, and the total above already speaks
 * for all of them.
 */
const MAX_LISTED = 5;

/** Human-facing label for an arrival with no note of its own. */
const KIND_LABEL: Record<Transaction["kind"], string> = {
  deposit: "Money in",
  withdrawal: "Spent",
  allowance: "Allowance",
  adjustment: "Adjustment",
  transfer: "Moved",
};

/**
 * One slice of the pie, already reduced to a percentage.
 */
interface Slice {
  name: string;
  percent: number;
  color: string;
  cents: number;
}

/**
 * Builds the Save-vs-Spend split from a balance.
 *
 * Percentages are computed from the two bucket totals rather than from the
 * arriving transaction, so the chart answers "where is my money now", which is
 * the question a pie chart is actually good at. Returns null when there is
 * nothing to divide — a pie of zero is meaningless.
 */
function buildSlices(balance: Balance): Slice[] | null {
  const wanted = ["save", "spend"];
  const buckets = balance.byCategory
    .filter((b) => wanted.includes(b.kind) && b.balanceCents > 0)
    .map((b) => ({
      name: b.name,
      cents: b.balanceCents,
      color: b.kind === "save" ? "var(--c-save)" : "var(--c-spend)",
    }));

  const total = buckets.reduce((sum, b) => sum + b.cents, 0);
  if (total <= 0 || buckets.length === 0) return null;

  // Largest-remainder rounding, so the labels always add to exactly 100% —
  // "60% / 41%" on screen would undermine the whole point of showing a split.
  const exact = buckets.map((b) => (b.cents * 100) / total);
  const floors = exact.map(Math.floor);
  let leftover = 100 - floors.reduce((a, b) => a + b, 0);

  const order = exact
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);

  const percents = [...floors];
  for (const { index } of order) {
    if (leftover <= 0) break;
    percents[index] = (percents[index] ?? 0) + 1;
    leftover -= 1;
  }

  return buckets.map((b, i) => ({ ...b, percent: percents[i] ?? 0 }));
}

/**
 * An SVG donut chart. Drawn with stroke-dasharray on concentric circles, which
 * needs no path maths and no charting library.
 */
function Pie({ slices }: { slices: Slice[] }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;

  return (
    <svg
      className="celebrate__pie"
      viewBox="0 0 100 100"
      role="img"
      aria-label={slices
        .map((s) => `${s.name} ${s.percent} percent`)
        .join(", ")}
    >
      {slices.map((slice) => {
        const length = (slice.percent / 100) * circumference;
        const circle = (
          <circle
            key={slice.name}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={slice.color}
            strokeWidth="16"
            strokeDasharray={`${length} ${circumference - length}`}
            // Start at twelve o'clock and lay each slice after the last.
            strokeDashoffset={-offset}
            transform="rotate(-90 50 50)"
          />
        );
        offset += length;
        return circle;
      })}
    </svg>
  );
}

/**
 * The itemised list of what arrived.
 *
 * A total alone answers "how much" but not "where from", and after a few days
 * away those are different questions — two weeks of allowance and a birthday
 * deposit is a story, not a number. One list with one animation, rather than a
 * popup per transaction.
 */
function Arrivals({
  transactions,
  categories,
}: {
  transactions: Transaction[];
  categories: Category[];
}) {
  const byId = new Map(categories.map((c) => [c.id, c]));
  // Newest first here, unlike the server's oldest-first order: the most recent
  // arrival is the one a child is looking for.
  const ordered = [...transactions].reverse();
  const listed = ordered.slice(0, MAX_LISTED);
  const rest = ordered.slice(MAX_LISTED);
  const restCents = rest.reduce((sum, t) => sum + t.amountCents, 0);

  return (
    <ul className="celebrate__arrivals">
      {listed.map((tx) => {
        const bucket = tx.categoryId ? byId.get(tx.categoryId) : undefined;
        return (
          <li key={tx.id} className="celebrate__arrival">
            <span className="celebrate__arrival-what">
              {tx.note || KIND_LABEL[tx.kind]}
            </span>
            <span className="celebrate__arrival-amount">
              +{formatCents(tx.amountCents)}
            </span>
            <span className="celebrate__arrival-when">
              {formatDate(tx.occurredAt)}
              {bucket ? ` · into ${bucket.name}` : ""}
            </span>
          </li>
        );
      })}

      {rest.length > 0 ? (
        <li className="celebrate__arrival celebrate__arrival--more">
          <span className="celebrate__arrival-what">
            and {rest.length} more
          </span>
          <span className="celebrate__arrival-amount">
            +{formatCents(restCents)}
          </span>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * The green "you got money!" celebration.
 *
 * Shown when the server reports money that arrived since the child last
 * dismissed one of these — which, unlike the old in-tab marker, includes money
 * that landed while they were logged out. It names the total, lists what made
 * it up, then shows how the child's money is now divided between Save and
 * Spend, which turns an arrival into a small lesson about saving.
 *
 * Dismissing is what marks the money seen, so a celebration that is never
 * looked at is still waiting at the next login.
 */
export function MoneyArrived({
  amountCents,
  transactions,
  categories,
  balance,
  onDismiss,
}: {
  amountCents: number;
  transactions: Transaction[];
  categories: Category[];
  balance: Balance;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, AUTO_DISMISS_MS);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("keydown", onKey);
    };
  }, [onDismiss]);

  const slices = buildSlices(balance);
  const many = transactions.length > 1;

  return (
    <div className="celebrate-backdrop" onClick={onDismiss} role="presentation">
      <div
        className="celebrate"
        role="alertdialog"
        aria-label={`You got ${formatCents(amountCents)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="celebrate__burst" aria-hidden="true">
          {/* Purely decorative confetti; hidden from assistive tech. */}
          {Array.from({ length: 9 }, (_, i) => (
            <span key={i} className={`confetti confetti--${i % 3}`} />
          ))}
        </div>

        <span className="celebrate__eyebrow">
          {many ? "You got money while you were away!" : "You got money!"}
        </span>
        <span className="celebrate__amount">+{formatCents(amountCents)}</span>

        {transactions.length > 0 ? (
          <Arrivals transactions={transactions} categories={categories} />
        ) : null}

        {slices ? (
          <div className="celebrate__chart">
            <Pie slices={slices} />
            <ul className="celebrate__legend">
              {slices.map((slice) => (
                <li key={slice.name} className="celebrate__legend-item">
                  <span
                    className="celebrate__swatch"
                    style={{ background: slice.color }}
                    aria-hidden="true"
                  />
                  <span className="celebrate__legend-name">{slice.name}</span>
                  <span className="celebrate__legend-percent">
                    {slice.percent}%
                  </span>
                  <span className="celebrate__legend-amount">
                    {formatCents(slice.cents)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <button type="button" className="btn celebrate__btn" onClick={onDismiss}>
          Nice!
        </button>
      </div>
    </div>
  );
}
